import type { MouseEvent } from 'react'
import {
  CameraOutlined,
  DeleteOutlined,
  EditOutlined,
  InboxOutlined,
  MoreOutlined,
  RollbackOutlined,
} from '@ant-design/icons'
import { Checkbox, Dropdown, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { MenuProps } from 'antd'
import { Button } from '@crosscode/gravity-components'
import type { CashTerminal } from './types'
import { isUkm5, terminalLabel } from './utils'
import styles from '@/pages/CashPage.module.css'

interface Props {
  rows: CashTerminal[]
  archiveTab: boolean
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleAll: (ids: string[], checked: boolean) => void
  onEdit: (t: CashTerminal) => void
  onArchive: (t: CashTerminal) => void
  onRestore: (t: CashTerminal) => void
  onDelete: (t: CashTerminal) => void
  onCopyIp: (ip?: string) => void
}

function RowMenu({
  terminal,
  archiveTab,
  onEdit,
  onArchive,
  onRestore,
  onDelete,
}: {
  terminal: CashTerminal
  archiveTab: boolean
  onEdit: (t: CashTerminal) => void
  onArchive: (t: CashTerminal) => void
  onRestore: (t: CashTerminal) => void
  onDelete: (t: CashTerminal) => void
}) {
  const items: MenuProps['items'] = archiveTab
    ? [
        {
          key: 'restore',
          icon: <RollbackOutlined />,
          label: 'Восстановить',
          onClick: () => onRestore(terminal),
        },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Удалить навсегда',
          danger: true,
          onClick: () => onDelete(terminal),
        },
      ]
    : [
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Изменить',
          onClick: () => onEdit(terminal),
        },
        {
          key: 'archive',
          icon: <InboxOutlined />,
          label: 'В архив',
          onClick: () => onArchive(terminal),
        },
      ]

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button
        size="small"
        icon={<MoreOutlined />}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      />
    </Dropdown>
  )
}

export function CashManageTable({
  rows,
  archiveTab,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onEdit,
  onArchive,
  onRestore,
  onDelete,
  onCopyIp,
}: Props) {
  const allIds = rows.map((r) => String(r.id))
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id))
  const someSelected = allIds.some((id) => selectedIds.has(id))

  const columns: ColumnsType<CashTerminal> = [
    {
      title: (
        <Checkbox
          indeterminate={someSelected && !allSelected}
          checked={allSelected}
          onChange={(e) => onToggleAll(allIds, e.target.checked)}
        />
      ),
      width: 44,
      render: (_, r) => (
        <Checkbox
          checked={selectedIds.has(String(r.id))}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect(String(r.id))}
        />
      ),
    },
    {
      title: 'Касса',
      width: 100,
      render: (_, r) => <span className={styles.num}>{terminalLabel(r)}</span>,
    },
    {
      title: 'Тип',
      width: 96,
      render: (_, r) => (
        <span className={styles.typeChip}>{isUkm5(r.type) ? 'УКМ‑5' : 'УКМ‑4'}</span>
      ),
    },
    {
      title: 'IP',
      width: 140,
      render: (_, r) => (
        <Tooltip title="Скопировать">
          <button
            type="button"
            className={styles.ipBtn}
            onClick={(e) => {
              e.stopPropagation()
              onCopyIp(r.ip)
            }}
          >
            {r.ip || '—'}
          </button>
        </Tooltip>
      ),
    },
    {
      title: 'Камера',
      width: 72,
      align: 'center',
      render: (_, r) =>
        r.trassir_channel_guid ? (
          <Tooltip title={r.trassir_channel_name || r.trassir_channel_guid}>
            <span className={styles.cameraIconOk} aria-label="Камера привязана">
              <CameraOutlined />
            </span>
          </Tooltip>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      title: 'Комментарий',
      ellipsis: true,
      render: (_, r) =>
        r.comment ? (
          <span className={styles.metaText}>{r.comment}</span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    ...(archiveTab
      ? [
          {
            title: 'В архиве с',
            width: 160,
            ellipsis: true,
            render: (_: unknown, r: CashTerminal) => (
              <span className={styles.muted}>
                {r.archivedAt ? new Date(r.archivedAt).toLocaleString('ru-RU') : '—'}
                {r.archivedBy ? ` · ${r.archivedBy}` : ''}
              </span>
            ),
          } as ColumnsType<CashTerminal>[number],
        ]
      : []),
    {
      title: '',
      width: 48,
      align: 'right',
      render: (_, r) => (
        <RowMenu
          terminal={r}
          archiveTab={archiveTab}
          onEdit={onEdit}
          onArchive={onArchive}
          onRestore={onRestore}
          onDelete={onDelete}
        />
      ),
    },
  ]

  return (
    <div className={styles.manageTableWrap}>
      <Table
        size="middle"
        pagination={false}
        rowKey={(r) => String(r.id)}
        dataSource={rows}
        columns={columns}
        tableLayout="fixed"
        className={styles.manageTable}
        locale={{ emptyText: archiveTab ? 'Архив пуст' : 'Нет касс' }}
        onRow={(record) => ({
          onClick: () => onToggleSelect(String(record.id)),
          className: selectedIds.has(String(record.id)) ? styles.manageRowSelected : undefined,
        })}
      />
    </div>
  )
}
